import { useEffect, useId, useRef, useState } from 'react'
import { Box, Chip, IconButton, Paper, Stack, Typography } from '@mui/material'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import type { MapLayerDataset, MapLayerVariant } from './catalogue'
import { variantCaption } from './catalogue'
import { observeMapResize } from './observeMapResize'

export const mapControlIconStyle = { '&&': { width: 30, height: 30, borderRadius: 1, color: 'text.secondary' } }

export default function MapLayerLegend({
  selections,
  width,
  maxHeight,
  onSpaceChange,
  mapContainer,
}: {
  selections: { dataset: MapLayerDataset; variant: MapLayerVariant }[]
  width: number
  maxHeight: number
  onSpaceChange: (height: number) => void
  mapContainer?: HTMLElement
}) {
  const [expanded, setExpanded] = useState(true)
  const contentId = useId()
  const panelRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const panel = panelRef.current
    if (!panel) return
    let previousSpace: number | undefined
    return observeMapResize(mapContainer ? [panel, mapContainer] : [panel], () => {
      if (!panel.isConnected) return
      const space = Math.ceil(
        mapContainer
          ? mapContainer.getBoundingClientRect().bottom - panel.getBoundingClientRect().top
          : panel.clientHeight,
      )
      if (space !== previousSpace) {
        previousSpace = space
        onSpaceChange(space)
      }
    })
  }, [mapContainer, onSpaceChange])
  return (
    <Paper
      ref={panelRef}
      component="section"
      aria-label="Map legend"
      elevation={4}
      sx={{ width, borderRadius: 2, overflow: 'hidden' }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 0.5 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="subtitle2" fontWeight={800}>
            Legend
          </Typography>
          <Chip label={selections.length} size="small" sx={{ height: 20 }} />
        </Stack>
        <IconButton
          aria-label={expanded ? 'Minimise legend' : 'Expand legend'}
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setExpanded(value => !value)}
          sx={mapControlIconStyle}
        >
          {expanded ? <ExpandMoreRoundedIcon /> : <ExpandLessRoundedIcon />}
        </IconButton>
      </Stack>
      <Stack
        id={contentId}
        hidden={!expanded}
        spacing={2}
        sx={{ display: expanded ? 'flex' : 'none', maxHeight, overflowY: 'auto', p: 1.5, pt: 0.5 }}
      >
        {selections.map(({ dataset, variant }) => {
          const variable = dataset.variables[variant.values.layer]
          return (
            <Stack key={dataset.id} spacing={0.75}>
              <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2" fontWeight={800}>
                  {dataset.label}
                </Typography>
                {dataset.tags.map(tag => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 19, fontSize: 10 }} />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {variantCaption(dataset, variant)}
                {variable.units ? ` · ${variable.units}` : ''}
              </Typography>
              {variable.legend.items.map((item, index) => (
                <Stack key={index} direction="row" gap={1} alignItems="center">
                  <Box
                    aria-hidden
                    sx={{
                      width: 24,
                      height: 14,
                      borderRadius: 0.5,
                      flexShrink: 0,
                      bgcolor: item.symbol ? 'transparent' : item.color,
                      opacity: item.opacity ?? 1,
                      textAlign: 'center',
                      lineHeight: '14px',
                    }}
                  >
                    {item.symbol === 'arrow' ? '→' : null}
                  </Box>
                  <Typography variant="caption">{item.label}</Typography>
                </Stack>
              ))}
            </Stack>
          )
        })}
      </Stack>
    </Paper>
  )
}
